<!-- Copyright (c) 2026 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="manage-main">
    <div class="left-menu">
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>

    <div class="manage-workspace">
      <div v-if="activePage == 'coefficientSettings'" class="page-content">
        <div class="content-header">
          <div>Coefficient Settings</div>
        </div>

        <div class="coefficient-top">
          <BoxContainer class="price-panel" title="Price Coefficient">
            <div class="table-wrap cipl-table-wrap">
              <table class="data-table price-grid">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Job Type</th>
                    <th>Price Coefficient</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in priceCoefficients" :key="row['#']">
                    <td>{{ row['#'] }}</td>
                    <td>{{ row['Job Type'] }}</td>
                    <td>
                      <input v-model="row['Price Coefficient']" :disabled="!editMode.coefficientSettings" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <RadioButtonSet
              title="Use Price Coeff for Driver Inc?"
              :buttons="driverCoeffOptions"
              labelKey="label"
              :selected.sync="selectedDriverCoeffMode"
              :isHorizontal="true"
              :showBox="false"
              :isDisabled="!editMode.coefficientSettings"
              @radioChanged="driverCoeffModeChanged"
            />
          </BoxContainer>

          <BoxContainer class="movement-panel" title="Movement Type Coefficient">
            <div class="table-wrap cipl-table-wrap">
              <table class="data-table coefficient-grid">
                <thead>
                  <tr>
                    <th>Job Type</th>
                    <th v-for="movement in movementTypes" :key="movement">{{ movement }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in movementCoefficients" :key="row['Job Type']">
                    <td>{{ row['Job Type'] }}</td>
                    <td v-for="movement in movementTypes" :key="movement">
                      <input v-model="row[movement]" :disabled="!editMode.coefficientSettings" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </BoxContainer>
        </div>

        <div class="bottom-actions">
          <button @click="newCommission">New</button>
          <button @click="startEdit('coefficientSettings')">Edit</button>
          <button @click="deleteSelected('coefficientSettings')">Delete</button>
          <button :disabled="!editMode.coefficientSettings" @click="saveSection('coefficientSettings')">Save</button>
          <button :disabled="!editMode.coefficientSettings" @click="cancelEdit('coefficientSettings')">Cancel</button>
        </div>
      </div>

      <div v-if="activePage == 'driverIncentive'" class="page-content">
        <div class="content-header">
          <div>Driver Incentive</div>
        </div>

        <div v-if="driverTabIndex != 0" class="top-selector">
          <InputQuery
            placeholder="Commission Code"
            :items="commissionList"
            itemKey="Code - Description"
            :selectedObject.sync="selectedCommission"
            :isDisabled="editMode.revenueMovement || editMode.nonRevenue || editMode.fixedZone || editMode.incremental"
            @selected="commissionSelected"
          />
        </div>

        <div v-else class="top-selector driver-commission-selector">
          <!-- for Edit -->
          <InputQuery
            v-if="formMode.driverCommission != 'new'"
            key="driver-commission-existing"
            class="commission-code-input"
            placeholder="Commission Code"
            :items="driverCommissionList"
            itemKey="Com Code"
            :selectedObject.sync="selectedDriverCommission"
            :isDisabled="editMode.driverCommission"
            @selected="driverCommissionSelected"
          />

          <!-- for New -->
          <InputQuery
            v-else
            key="driver-commission-new"
            class="commission-code-input"
            placeholder="Commission Code"
            :items="[]"
            itemKey="Com Code"
            :search.sync="driverCommissionCode"
            :allowFreeText="true"
            validator="text,10,uppercase,alphanumeric"
          />
          <Input
            class="commission-description-input"
            placeholder="Description"
            :inputValue.sync="driverCommissionDescription"
            :isDisabled="!editMode.driverCommission"
          />
        </div>
        <Tab class="driver-incentive-tab" :tabs="driverIncentiveTabs" :tabIndex.sync="driverTabIndex">
          <div slot="Driver Commission Structure" class="driver-tab-page">
            <BoxContainer
              v-if="coefficientSettingsDetail.ComHeader"
              class="coefficient-settings-detail coefficient-settings-bottom"
              :title="
                formMode.driverCommission == 'new' ? 'New Driver Commission Structure'
                : formMode.driverCommission == 'edit' ? 'Edit Driver Commission Structure'
                : 'Driver Commission Structure'
              "
            >
              <div class="field-grid three">
                <Input
                  placeholder="Driver Basic Pay (RM)"
                  :inputValue.sync="coefficientSettingsDetail.ComHeader.Basic_Pay1"
                  validator="positiveDecimal,19,2"
                  :isDisabled="!editMode.driverCommission"
                />
                <Input
                  placeholder="Attendant Basic Pay (RM)"
                  :inputValue.sync="coefficientSettingsDetail.ComHeader.Basic_Pay2"
                  validator="positiveDecimal,19,2"
                  :isDisabled="!editMode.driverCommission"
                />
              </div>

              <BoxContainer title="Monthly Bonus Incentive">
                <div class="formula-row">
                  <span>If comm. RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.Range"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span>every RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.IncEvery"
                    validator="positiveDecimal,11,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <Input
                    class="formula-input"
                    placeholder="Driver"
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.Driver"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.monthlyBonus.Qlf }} </span>
                  <Input
                    class="formula-input"
                    placeholder="Attendant"
                    :inputValue.sync="coefficientSettingsDetail.monthlyBonus.Attendant"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.monthlyBonus.Qlf }} </span>
                  <Dropdown
                    class="formula-dropdown"
                    placeholder="Type"
                    :items="amountTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="selectedMonthlyType"
                    @selected="commissionTypeSelected('monthlyBonus', $event)"
                    :isDisabled="!editMode.driverCommission"
                  />
                </div>
              </BoxContainer>

              <BoxContainer title="Reserve (Tabung)">
                <div class="formula-row">
                  <span>If comm. RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.reserve.Range"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span>every RM</span>
                  <Input
                    class="formula-input"
                    placeholder=""
                    :inputValue.sync="coefficientSettingsDetail.reserve.IncEvery"
                    validator="positiveDecimal,11,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <Input
                    class="formula-input"
                    placeholder="Driver"
                    :inputValue.sync="coefficientSettingsDetail.reserve.Driver"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.reserve.Qlf }} </span>
                  <Input
                    class="formula-input"
                    placeholder="Attendant"
                    :inputValue.sync="coefficientSettingsDetail.reserve.Attendant"
                    validator="positiveDecimal,19,2"
                    :isDisabled="!editMode.driverCommission"
                  />
                  <span class="amount-symbol"> {{ coefficientSettingsDetail.reserve.Qlf }} </span>
                  <Dropdown
                    placeholder="Type"
                    :items="amountTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="selectedReserveType"
                    @selected="commissionTypeSelected('reserve', $event)"
                    :isDisabled="!editMode.driverCommission"
                  />
                </div>
              </BoxContainer>
            </BoxContainer>
            <div v-else class="commission-loading"> Loading commission details . . . </div>

            <!-- <div class="bottom-actions">
              <button @click="saveSection('revenueMovement')">Save</button>
              <button @click="cancelEdit('revenueMovement')">Cancel</button>
            </div> -->
            <div class="bottom-actions">
              <template v-if="!editMode.driverCommission">
                <button @click="newDriverCommission"> New </button>

                <button :disabled="!coefficientSettingsDetail.ComHeader" @click="editDriverCommission"> Edit </button>

                <button :disabled="!coefficientSettingsDetail.ComHeader" @click="deleteDriverCommission">
                  Delete
                </button>
              </template>

              <template v-else>
                <button @click="saveDriverCommission">
                  {{ formMode.driverCommission == 'new' ? 'Save New' : 'Save Edit' }}
                </button>

                <button @click="cancelDriverCommission"> Cancel </button>
              </template>
            </div>
          </div>

          <div slot="Revenue Movement Incentive" class="driver-tab-page">
            <BoxContainer class="fill-panel" title="Revenue Movement Incentive Coefficient">
              <div class="table-wrap cipl-table-wrap">
                <table class="data-table incentive-grid">
                  <thead>
                    <tr>
                      <th>Job Type</th>
                      <th v-for="movement in movementTypes" :key="movement">{{ movement }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="tableLoading" class="table-message-row">
                      <td :colspan="movementTypes.length + 1" class="table-message-cell">
                        Loading Revenue Movement Incentive Coefficient data . . .
                      </td>
                    </tr>

                    <tr v-else-if="revMvtIncDet.length == 0" class="table-message-row">
                      <td :colspan="movementTypes.length + 1" class="table-message-cell"> No items </td>
                    </tr>

                    <tr v-else v-for="row in revMvtIncDet" :key="row['Job Type']">
                      <td>{{ row['Job Type'] }}</td>

                      <td v-for="movement in movementTypes" :key="movement">
                        <div class="amount-cell">
                          <Input
                            placeholder=""
                            :inputValue.sync="row[movement].value"
                            :isDisabled="row[movement].disabled || !editMode.revenueMovement"
                            :validator="getAmountValidator(row[movement])"
                            @blur="formatAmountCell(row[movement])"
                          />

                          <Dropdown
                            :class="['type-dropdown', { 'dropdown-open-left': isLastMovement(movement) }]"
                            placeholder=""
                            :items="amountTypeOptions"
                            itemKey="label"
                            :selectedObject.sync="row[movement].selectedType"
                            :isDisabled="row[movement].disabled || !editMode.revenueMovement"
                            @selected="amountTypeSelected(row[movement], $event)"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="diversion-row">
                <label v-for="d in diversionDet" :key="d.key">
                  <span>{{ d.key }}</span>

                  <Input
                    placeholder=""
                    :inputValue.sync="d.value"
                    :isDisabled="tableLoading || !editMode.revenueMovement"
                    :validator="getAmountValidator(d)"
                    @blur="formatAmountCell(d)"
                  />

                  <Dropdown
                    placeholder=""
                    :items="amountTypeOptions"
                    itemKey="label"
                    :selectedObject.sync="d.selectedType"
                    :isDisabled="tableLoading || !editMode.revenueMovement"
                    @selected="amountTypeSelected(d, $event)"
                  />
                </label>
              </div>
            </BoxContainer>

            <div class="bottom-actions">
              <!-- <button @click="saveSection('revenueMovement')">Save</button>
              <button @click="cancelEdit('revenueMovement')">Cancel</button> -->
              <template v-if="!editMode.revenueMovement">
                <button :disabled="tableLoading || !selectedCommission" @click="editRevenueMovement"> Edit </button>
              </template>

              <template v-else>
                <button @click="saveRevenueMovement">
                  {{ formMode.revenueMovement == 'new' ? 'Save New' : 'Save Edit' }}
                </button>

                <button @click="cancelRevenueMovement"> Cancel </button>
              </template>
            </div>
          </div>

          <div slot="Non-Rev Mvt Inc (Fix)" class="driver-tab-page split-view">
            <div class="list-side">
              <div class="tablebox-area">
                <TableBox
                  v-if="!tableLoading"
                  title="Non-Revenue Movement Incentive"
                  :tableData.sync="nonRevIncList"
                  :selectedRow.sync="selectedNonRevenueRows"
                />
              </div>
            </div>

            <div class="right-menu">
              <button :disabled="editMode.nonRevenue" @click="addRow('nonRevenue')">New</button>
              <transition name="details-expand">
                <BoxContainer
                  v-if="editMode.nonRevenue"
                  class="details-panel"
                  :title="formMode.nonRevenue == 'new' ? 'New Non-Revenue Details' : 'Edit Non-Revenue Details'"
                >
                  <div class="details-form">
                    <Input
                      placeholder="Movement Type"
                      :inputValue.sync="selectedNonRevenue['Movement']"
                      :isDisabled="formMode.nonRevenue == 'edit'"
                    />
                    <Input placeholder="Description" :inputValue.sync="selectedNonRevenue['Description']" />
                    <div class="details-rate-row">
                      <span>Driver</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedNonRevenue['Driver Rate($)']"
                        validator="postiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedNonRevenue['Driver Rate(%)']"
                        validator="postiveDecimal,11,2"
                      />
                      <div style="grid-column: 2 / -1; width: 75%">
                        <Dropdown
                          placeholder="From/To Tariff"
                          style="width: 100%"
                          :items="tariffDropdown"
                          itemKey="text"
                          :selectedObject.sync="selectedNonRevenueTariff"
                        />
                      </div>
                    </div>
                    <div class="details-rate-row">
                      <span>Attendant</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedNonRevenue['Attendant Rate($)']"
                        validator="postiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedNonRevenue['Attendant Rate(%)']"
                        validator="postiveDecimal,11,2"
                      />
                      <div style="grid-column: 2 / -1; width: 75%">
                        <Dropdown
                          placeholder="From/To Tariff"
                          :items="tariffDropdown"
                          itemKey="text"
                          :selectedObject.sync="selectedNonRevenueAttTariff"
                        />
                      </div>
                    </div>
                  </div>
                </BoxContainer>
              </transition>
              <div class="bottom-actions vertical">
                <template v-if="!editMode.nonRevenue">
                  <button @click="editNonRevenue" :disabled="selectedNonRevenueRows.length == 0"> Edit </button>

                  <button @click="deleteNonRevenue" :disabled="selectedNonRevenueRows.length == 0"> Delete </button>
                </template>

                <template v-else>
                  <button @click="saveNonRevenue">
                    {{ formMode.nonRevenue == 'new' ? 'Save New' : 'Save Edit' }}
                  </button>

                  <button @click="cancelNonRevenue"> Cancel </button>
                </template>
              </div>
            </div>
          </div>

          <div slot="Fixed-Zone Incentive" class="driver-tab-page split-view">
            <div class="list-side">
              <div class="tablebox-area">
                <TableBox
                  v-if="!tableLoading"
                  title="Fixed-Zone Incentive"
                  :tableData="fixedZoneIncentives"
                  :selectedRow.sync="selectedFixedZoneRows"
                />
              </div>
            </div>

            <div class="right-menu">
              <button :disabled="editMode.fixedZone" @click="addRow('fixedZone')">New</button>
              <transition name="details-expand">
                <BoxContainer
                  v-if="editMode.fixedZone"
                  class="details-panel"
                  :title="formMode.fixedZone == 'new' ? 'New Zone Details' : 'Edit Zone Details'"
                >
                  <div class="details-form">
                    <div class="zone-row">
                      <InputQuery
                        placeholder="From Zone"
                        :items="zoneList"
                        itemKey="Code - Description"
                        :selectedObject.sync="selectedFixedFromZone"
                        :isDisabled="formMode.fixedZone == 'edit'"
                      />
                    </div>
                    <div class="zone-row">
                      <InputQuery
                        placeholder="To Zone"
                        :items="zoneList"
                        itemKey="Code - Description"
                        :selectedObject.sync="selectedFixedToZone"
                        :isDisabled="formMode.fixedZone == 'edit'"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Driver</span>
                      <Input placeholder="$" :inputValue.sync="selectedFixedZone['Driver']" />
                      <span>+</span>
                      <Input placeholder="%" :inputValue.sync="selectedFixedZone['Driver Other Inc']" />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Attendant</span>
                      <Input placeholder="$" :inputValue.sync="selectedFixedZone['Attendant']" />
                      <span>+</span>
                      <Input placeholder="%" :inputValue.sync="selectedFixedZone['Attendant Other Inc']" />
                    </div>
                    <Input placeholder="B2B Coeff" :inputValue.sync="selectedFixedZone['B2B Coeff']" />
                  </div>
                </BoxContainer>
              </transition>
              <div class="bottom-actions vertical">
                <template v-if="!editMode.fixedZone">
                  <button @click="editFixedZone" :disabled="selectedFixedZoneRows.length == 0"> Edit </button>

                  <button @click="deleteFixedZone" :disabled="selectedFixedZoneRows.length == 0"> Delete </button>
                </template>

                <template v-else>
                  <button @click="saveFixedZone">
                    {{ formMode.fixedZone == 'new' ? 'Save New' : 'Save Edit' }}
                  </button>

                  <button @click="cancelFixedZone"> Cancel </button>
                </template>
              </div>
            </div>
          </div>

          <div slot="Add Incremental Incentive" class="driver-tab-page split-view">
            <div class="list-side">
              <div class="tablebox-area">
                <TableBox
                  v-if="!tableLoading"
                  title="Add Incremental Incentive"
                  :tableData="incrementalIncentives"
                  :selectedRow.sync="selectedIncrementalRows"
                  :hideColumns="['IncrementType']"
                />
              </div>
            </div>

            <div class="right-menu">
              <button :disabled="editMode.incremental" @click="addRow('incremental')">New</button>
              <transition name="details-expand">
                <BoxContainer
                  v-if="editMode.incremental"
                  class="details-panel"
                  :title="formMode.incremental == 'new' ? 'New Increment Details' : 'Edit Increment Details'"
                >
                  <div class="details-form">
                    <Input
                      placeholder="Trip From"
                      :inputValue.sync="selectedIncremental['Trip From']"
                      validator="positiveInteger"
                      :isDisabled="formMode.incremental == 'edit'"
                    />
                    <div class="details-rate-row compact-rate">
                      <span>Driver</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Rate($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Rate(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Attendant</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Attendant Rate($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Attendant Rate(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Drv Local</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Local($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Local(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Drv Medium</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Medium($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Medium(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                    <div class="details-rate-row compact-rate">
                      <span>Drv Outstn</span>
                      <Input
                        placeholder="$"
                        :inputValue.sync="selectedIncremental['Driver Outstation($)']"
                        validator="positiveDecimal,11,2"
                      />
                      <span>+</span>
                      <Input
                        placeholder="%"
                        :inputValue.sync="selectedIncremental['Driver Outstation(%)']"
                        validator="positiveDecimal,11,2"
                      />
                    </div>
                  </div>
                  <RadioButtonSet
                    title="Type"
                    :buttons="incrementTypeOptions"
                    labelKey="label"
                    :selected.sync="selectedIncrementType"
                    :isHorizontal="true"
                    :showBox="false"
                    @radioChanged="incrementTypeChanged"
                  />
                </BoxContainer>
              </transition>
              <div class="bottom-actions vertical">
                <template v-if="!editMode.incremental">
                  <button @click="editIncremental" :disabled="selectedIncrementalRows.length == 0"> Edit </button>

                  <button @click="deleteIncremental" :disabled="selectedIncrementalRows.length == 0"> Delete </button>
                </template>

                <template v-else>
                  <button @click="saveIncremental">
                    {{ formMode.incremental == 'new' ? 'Save New' : 'Save Edit' }}
                  </button>

                  <button @click="cancelIncremental"> Cancel </button>
                </template>
              </div>
            </div>
          </div>
        </Tab>
      </div>

      <div v-if="activePage == 'specialDay'" class="page-content split-view">
        <div class="list-side">
          <div class="content-header">
            <div>Special Day Incentive</div>
          </div>
          <div class="top-selector">
            <InputQuery
              placeholder="Commission Code"
              :items="commissionList"
              itemKey="Code - Description"
              descKey="Description"
              :selectedObject.sync="selectedSpecialDayCommission"
            />
          </div>
          <div class="tablebox-area">
            <TableBox
              title="Special Day Incentive"
              :tableData="specialDayIncentives"
              :selectedRow.sync="selectedSpecialDayRows"
            />
          </div>
        </div>

        <div class="right-menu">
          <button @click="addRow('specialDay')">New</button>
          <BoxContainer class="details-panel" title="Special Day Details">
            <div class="field-grid one">
              <Dropdown
                placeholder="Occurrence Type"
                :items="occurrenceOptions"
                itemKey="label"
                :selectedObject.sync="selectedOccurrenceType"
              />
              <DateTimePicker placeholder="Date" title="Date" :showTime="false" :value.sync="selectedSpecialDayDate" />
              <Dropdown
                placeholder="Remark"
                :items="dayRemarkOptions"
                itemKey="label"
                :selectedObject.sync="selectedDayRemark"
              />
              <Dropdown
                placeholder="Movement Type"
                :items="specialMovementTypeOptions"
                itemKey="label"
                :selectedObject.sync="selectedSpecialMovementType"
              />
              <Dropdown
                placeholder="Movement Scope"
                :items="movementScopeOptions"
                itemKey="label"
                :selectedObject.sync="selectedMovementScope"
              />
              <Dropdown
                placeholder="Incentive Type"
                :items="incentiveTypeOptions"
                itemKey="label"
                :selectedObject.sync="selectedIncentiveType"
              />
              <Input placeholder="Incentive Value" :inputValue.sync="selectedSpecialDay['Incentive Value']" />
              <Dropdown placeholder="Base" :items="baseOptions" itemKey="label" :selectedObject.sync="selectedBase" />
            </div>
          </BoxContainer>
          <div class="bottom-actions vertical">
            <!-- <button @click="addRow('specialDay')">New</button> -->
            <button @click="startEdit('specialDay')">Edit</button>
            <button @click="deleteSelected('specialDay')">Delete</button>
            <!-- <button @click="saveSection('specialDay')">Save</button> -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  data: function () {
    return {
      activePage: 'driverIncentive',
      driverTabIndex: 0,
      driverCoeffMode: 'No',
      selectedDriverCoeffMode: { label: 'No' },
      selectedNonRevenueRows: [],
      selectedFixedZoneRows: [],
      selectedIncrementalRows: [],
      selectedSpecialDayRows: [],
      editMode: {
        coefficientSettings: false,
        driverCommission: false,
        revenueMovement: false,
        specialDay: false,
        nonRevenue: false,
        fixedZone: false,
        incremental: false,
      },
      formMode: {
        driverCommission: '',
        revenueMovement: '',
        nonRevenue: '',
        fixedZone: '',
        incremental: '',
      },
      menu: [
        {
          title: 'Coefficient Settings',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-todo-list-64.png',
          enabled: false,
          // key: 'SSTab1',
        },
        {
          title: 'Driver Incentive',
          icon: 'https://dsmlib.logistics.systems/Icons/money.png',
          enabled: false,
          key: 'SSTab1_2',
        },
        {
          title: 'Special Day Incentive',
          icon: 'https://dsmlib.logistics.systems/Icons/Calendar.png',
          enabled: false,
          // key: 'SSTab1_3',
        },
      ],
      driverIncentiveTabs: [
        { title: 'Driver Commission Structure' },
        { title: 'Revenue Movement Incentive' },
        { title: 'Non-Rev Mvt Inc (Fix)' },
        { title: 'Fixed-Zone Incentive' },
        { title: 'Add Incremental Incentive' },
      ],
      commissionList: [],
      driverCommissionList: [],
      zoneList: [],
      selectedCommission: null,
      selectedDriverCommission: null,
      driverCommissionCode: '',
      driverCommissionDescription: '',
      selectedSpecialDayCommission: {
        Code: '10D',
        Description: '10 TONNER-DRI/ATT',
        'Code - Description': '10D - 10 TONNER-DRI/ATT',
      },
      selectedNonRevenueTariff: null,
      selectedNonRevenueAttTariff: null,
      selectedIncrementType: { label: 'Gradual Increment', value: 0 },
      selectedOccurrenceType: { label: 'Annually' },
      selectedDayRemark: { label: 'Labour Day' },
      selectedSpecialMovementType: { label: 'C1W' },
      selectedMovementScope: { label: 'Select by Movement' },
      selectedIncentiveType: { label: '%' },
      selectedBase: { label: 'of Driver Tariff Rate' },
      selectedSpecialDayDate: new Date(2010, 4, 1),
      selectedMonthlyType: { label: '$' },
      selectedReserveType: { label: '%' },
      amountTypeOptions: [{ label: '$' }, { label: '%' }],
      tariffDropdown: [
        { value: 0, text: '-' },
        { value: 1, text: "of 'From' Tariff" },
        { value: 2, text: "of 'To' Tariff" },
        { value: 3, text: 'of Additional Billing' },
        { value: 4, text: 'of Driver Tariff Rate' },
        { value: 5, text: 'of Driver Fixed Zone Rate' },
      ],
      driverCoeffOptions: [{ label: 'No' }, { label: 'Yes' }, { label: '1 Way Special Additional' }],
      incrementTypeOptions: [{label: 'Gradual Increment', value: 0 }, { label: 'Stage Increment', value: 1}],
      occurrenceOptions: [{ label: 'Annually' }, { label: 'Once' }, { label: 'Monthly' }],
      dayRemarkOptions: [{ label: 'Labour Day' }, { label: 'Public Holiday' }, { label: 'Special Event' }],
      specialMovementTypeOptions: [
        { label: 'C1W' },
        { label: 'CSE' },
        { label: 'D1W' },
        { label: 'DRD' },
        { label: 'ALLFixedZones' },
      ],
      movementScopeOptions: [
        { label: 'Select by Movement' },
        { label: 'All Movements' },
        { label: 'All Rev Movements' },
        { label: 'All Non-Rev Movements' },
        { label: 'All Fixed Zones' },
      ],
      incentiveTypeOptions: [{ label: '%' }, { label: '$' }],
      baseOptions: [{ label: 'of Driver Tariff Rate' }, { label: 'of Additional' }, { label: 'Fixed Amount' }],
      movementTypes: ['DRD', 'D1W', 'C1W', 'DSS', 'DSE', 'CSS', 'CSE'],
      diversionTypes: ['DV1', 'DV2', 'DV3', 'DV4'],
      priceCoefficients: [
        { '#': 1, 'Job Type': 'IMPORT', 'Price Coefficient': '1.00000' },
        { '#': 2, 'Job Type': 'EXPORT', 'Price Coefficient': '1.00000' },
        { '#': 3, 'Job Type': '1-WAY IMPORT', 'Price Coefficient': '0.66670' },
        { '#': 4, 'Job Type': '1-WAY EXPORT', 'Price Coefficient': '0.66670' },
        { '#': 5, 'Job Type': 'IMPORT-EXPORT', 'Price Coefficient': '1.33330' },
        { '#': 6, 'Job Type': 'EMPTY-REJECT', 'Price Coefficient': '0.66670' },
        { '#': 7, 'Job Type': 'REPOSITION', 'Price Coefficient': '1.00000' },
        { '#': 8, 'Job Type': '1-WAY IMP (MT)', 'Price Coefficient': '0.66670' },
        { '#': 9, 'Job Type': '1-WAY EXP (MT)', 'Price Coefficient': '0.66670' },
      ],
      movementCoefficients: [],
      coefficientSettingsDetail: {},
      driverCommissionBackup: null,
      tableLoading: false,
      revMvtIncList: [],
      revMvtIncDet: [],
      diversionDet: [],
      revenueMovementBackup: null,
      nonRevIncList: [],
      selectedNonRevenue: {},
      fixedZoneIncentives: [],
      selectedFixedZone: {},
      selectedFixedFromZone: null,
      selectedFixedToZone: null,
      incrementalIncentives: [],
      selectedIncremental: {},
      specialDayIncentives: [
        {
          ID: '20100001',
          'Commission Code': '10D',
          Occur: 'A',
          Day: '01/05/2010',
          'Day remarks': 'Labour Day',
          'Mvt Type': 'C1W',
          'Movement Scope': 'Select by Movement',
          'Incentive Value': '1.00',
          Type: '%',
          Base: 'of Driver Tariff Rate',
        },
        {
          ID: '20100002',
          'Commission Code': '10D',
          Occur: 'A',
          Day: '01/05/2010',
          'Day remarks': 'Labour Day',
          'Mvt Type': 'CSE',
          'Movement Scope': 'All Rev Movements',
          'Incentive Value': '1.00',
          Type: '%',
          Base: 'of Driver Tariff Rate',
        },
        {
          ID: '20100010',
          'Commission Code': '10D',
          Occur: 'A',
          Day: '01/05/2010',
          'Day remarks': 'Labour Day',
          'Mvt Type': 'ALLFixedZones',
          'Movement Scope': 'All Fixed Zones',
          'Incentive Value': '100.00',
          Type: '%',
          Base: 'of Driver Tariff Rate',
        },
      ],
      selectedSpecialDay: {},
    };
  },
  methods: {
    menuClicked: function (item) {
      switch (item.title) {
        case 'Coefficient Settings':
          this.activePage = 'coefficientSettings';
          break;

        case 'Driver Incentive':
          this.activePage = 'driverIncentive';
          break;

        case 'Special Day Incentive':
          this.activePage = 'specialDay';
          break;
      }
    },
    newDriverCommission: function () {
      this.driverCommissionBackup = {
        detail: JSON.parse(JSON.stringify(this.coefficientSettingsDetail || {})),
        selectedCommissionCode: this.selectedDriverCommission?.['Com Code'] || this.selectedCommission?.['Com Code'] || '',
        code: this.driverCommissionCode,
        description: this.driverCommissionDescription,
      };

      this.formMode.driverCommission = 'new';
      this.editMode.driverCommission = true;

      this.selectedDriverCommission = null;
      this.driverCommissionCode = '';
      this.driverCommissionDescription = '';

      const comHeader = {
        Com_Code: '',
        Remark: '',
        Basic_Pay1: '0.00',
        Basic_Pay2: '0.00',
        ComDetails: [],
      };

      this.coefficientSettingsDetail = this.mapCommissionStructure({
        ComHeader: comHeader,
      });

      this.coefficientSettingsDetail.monthlyBonus.Qlf = '$';
      this.coefficientSettingsDetail.reserve.Qlf = '%';

      this.selectedMonthlyType = { label: '$'};
      this.selectedReserveType = { label: '%'};
    },
    editDriverCommission: function () {
      if (!this.coefficientSettingsDetail.ComHeader) {
        return;
      }

      this.driverCommissionBackup = {
        detail: JSON.parse(JSON.stringify(this.coefficientSettingsDetail)),

        selectedCommissionCode:
          this.selectedCommission?.['Com Code'] || this.selectedDriverCommission?.['Com Code'] || '',

        code: this.driverCommissionCode,

        description: this.driverCommissionDescription,
      };

      this.formMode.driverCommission = 'edit';
      this.editMode.driverCommission = true;
    },
    cancelDriverCommission: function () {
      if (!this.driverCommissionBackup) {
        return;
      }

      const backup = this.driverCommissionBackup;

      this.coefficientSettingsDetail = JSON.parse(JSON.stringify(backup.detail || {}));

      this.editMode.driverCommission = false;
      this.formMode.driverCommission = '';

      this.syncCommissionSelectors(backup.selectedCommissionCode);

      this.driverCommissionCode = backup.code || backup.selectedCommissionCode || '';

      this.driverCommissionDescription = backup.description || '';

      if (this.coefficientSettingsDetail.monthlyBonus) {
        this.selectedMonthlyType = {
          label: this.coefficientSettingsDetail.monthlyBonus.Qlf || '$',
        };
      }

      if (this.coefficientSettingsDetail.reserve) {
        this.selectedReserveType = {
          label: this.coefficientSettingsDetail.reserve.Qlf || '%',
        };
      }

      this.driverCommissionBackup = null;
    },
    saveDriverCommission: async function () {
      const mode = this.formMode.driverCommission;
      const commissionCode = String(this.driverCommissionCode || '').trim();
      const description = String(this.driverCommissionDescription || '').trim();

      if (!commissionCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please enter a Commission Code.');

        return;
      }

      if (mode == 'new') {
        const duplicate = this.commissionList.some((item) => {
          return (
            String(item['Com Code'] || '')
              .trim()
              .toUpperCase() == commissionCode.toUpperCase()
          );
        });

        if (duplicate) {
          this.$showAlert('warning', 'Duplicate Commission Code', `Commission Code ${commissionCode} already exists.`);

          return;
        }
      }

      if (!description) {
        this.$showAlert('warning', 'Description Required', 'Please enter a Description.');

        return;
      }

      const commission = this.buildCommissionPayload();

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: commissionCode,
        remark: description,
        rangetype: String(commission.ComHeader.RangeType || 0),
        basicpay1: commission.ComHeader.Basic_Pay1,
        basicpay2: commission.ComHeader.Basic_Pay2,
        details: (commission.ComHeader.ComDetails || []).map((detail) => {
          return {
            comtype: String(detail.Com_Type || ''),
            seqno: String(detail.Seq_No || ''),
            range: detail.Range,
            incevery: detail.IncEvery,
            qlf: String(detail.Qlf || '$'),
            remarks: String(detail.Remarks || detail.Remark || ''),
            driver: detail.Driver,
            attendant: detail.Attendant,
          };
        }),
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Commission Structure' : 'Updating Commission Structure');

      const result = await this.$apiCallV2('/Manage/SaveDrvCom', requestBody);

      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);

        return;
      }

      this.editMode.driverCommission = false;
      this.formMode.driverCommission = '';
      this.driverCommissionBackup = null;

      await this.refreshCommissionList(commissionCode);

      this.$showAlert(
        'success',
        mode == 'new' ? 'Created' : 'Updated',
        result?.[0]?.['ErrMsg'] ||
          (mode == 'new' ?
            `Commission ${commissionCode} created successfully.`
          : `Commission ${commissionCode} updated successfully.`),
      );
    },

    deleteDriverCommission: async function () {
      if (!this.coefficientSettingsDetail.ComHeader) {
        return;
      }

      const comCode = this.getSelectedCommissionCode();

      if (!comCode) {
        return;
      }

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Commission Structure',
        `Are you sure you want to delete Commission Code <b>${comCode}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: comCode,
        remark: '',
        rangetype: '',
        basicpay1: '0.00',
        basicpay2: '0.00',
        details: [],
      };

      this.$fullscreenLoading('Deleting Commission Structure');

      const result = await this.$apiCallV2('/Manage/SaveDrvCom', requestBody);

      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);

        return;
      }

      await this.refreshCommissionList('');

      this.$showAlert(
        'success',
        'Commission Deleted',
        result?.[0]?.['ErrMsg'] || `Commission ${comCode} deleted successfully.`,
      );
    },

    editRevenueMovement: function () {
      if (!this.selectedCommission) {
        return;
      }

      this.revenueMovementBackup = {
        revMvtIncList: JSON.parse(JSON.stringify(this.revMvtIncList || [])),
        revMvtIncDet: JSON.parse(JSON.stringify(this.revMvtIncDet || [])),
        diversionDet: JSON.parse(JSON.stringify(this.diversionDet || [])),
      };

      this.formMode.revenueMovement = 'edit';
      this.editMode.revenueMovement = true;
    },

    cancelRevenueMovement: function () {
      if (this.revenueMovementBackup) {
        this.revMvtIncList = JSON.parse(JSON.stringify(this.revenueMovementBackup.revMvtIncList || []));
        this.revMvtIncDet = JSON.parse(JSON.stringify(this.revenueMovementBackup.revMvtIncDet || []));
        this.diversionDet = JSON.parse(JSON.stringify(this.revenueMovementBackup.diversionDet || []));
      }

      this.editMode.revenueMovement = false;
      this.formMode.revenueMovement = '';
      this.revenueMovementBackup = null;
    },

    saveRevenueMovement: async function () {
      const mode = this.formMode.revenueMovement;

      const comCode = this.getSelectedCommissionCode();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');

        return;
      }

      const incentives = this.buildRevenueMovementPayload();

      const requestBody = {
        comcode: comCode,

        incentives: incentives.map((item) => {
          return {
            jobtype: String(item.JobType || ''),
            movementtype: String(item.MovementType || ''),
            key: String(item.JTypeMvt || ''),
            type: String(item.IncentiveType || '$'),
            value: String(item.IncentiveValue || '0'),
          };
        }),
      };

      this.$fullscreenLoading('Updating Revenue Movement Incentive');

      const result = await this.$apiCallV2('/Manage/SaveRevMvtInc', requestBody);

      this.$fullscreenLoading();

      if (result && result[0] && result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);

        return;
      }

      this.editMode.revenueMovement = false;
      this.formMode.revenueMovement = '';
      this.revenueMovementBackup = null;

      const commission = this.commissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert(
        'success',
        'Updated',
        result?.[0]?.['ErrMsg'] || 'Revenue Movement Incentive updated successfully.',
      );
    },

    startEdit: function (section) {
      this.formMode[section] = 'edit';
      this.editMode[section] = true;
    },
    cancelEdit: function (section) {
      this.editMode[section] = false;
      this.formMode[section] = '';
      this.syncSelectedRows();
    },
    newCommission: function () {
      this.selectedMonthlyType = { label: '$' };
      this.selectedReserveType = { label: '%' };
      this.startEdit('coefficientSettings');
    },
    addRow: function (section) {
      this.formMode[section] = 'new';

      if (section == 'nonRevenue') {
        this.selectedNonRevenue = {};
        this.selectedNonRevenueTariff = null;
        this.selectedNonRevenueAttTariff = null;
      }

      if (section == 'fixedZone') {
        this.selectedFixedZone = {};
        this.selectedFixedFromZone = null;
        this.selectedFixedToZone = null;
      }

      if (section == 'incremental') {
        this.selectedIncremental = {};
        this.selectedIncrementType = { label: 'Gradual Increment', value: 0 };
      }

      this.editMode[section] = true;
    },
    saveSection: function (section) {
      this.editMode[section] = false;
      this.formMode[section] = '';

      if (this.$showAlert) {
        this.$showAlert('success', 'Saved', `${section} settings saved locally.`);
      }
    },
    editNonRevenue: function () {
      if (!this.selectedNonRevenueRows || this.selectedNonRevenueRows.length == 0) {
        return;
      }

      this.selectedNonRevenue = JSON.parse(JSON.stringify(this.selectedNonRevenueRows[0]));

      this.selectedNonRevenueTariff =
        this.tariffDropdown.find((item) => {
          return item.value == this.selectedNonRevenue['IncentivePercentType'];
        }) || null;

      this.selectedNonRevenueAttTariff =
        this.tariffDropdown.find((item) => {
          return item.value == this.selectedNonRevenue['AttnIncentivePercentType'];
        }) || null;

      this.formMode.nonRevenue = 'edit';
      this.editMode.nonRevenue = true;
    },

    cancelNonRevenue: function () {
      this.editMode.nonRevenue = false;
      this.formMode.nonRevenue = '';

      this.syncSelectedRows();

      const selectedRow = this.selectedNonRevenueRows[0];

      if (!selectedRow) {
        this.selectedNonRevenueTariff = null;
        this.selectedNonRevenueAttTariff = null;
        return;
      }

      this.selectedNonRevenueTariff =
        this.tariffDropdown.find((item) => {
          return item.value == selectedRow['IncentivePercentType'];
        }) || null;

      this.selectedNonRevenueAttTariff =
        this.tariffDropdown.find((item) => {
          return item.value == selectedRow['AttnIncentivePercentType'];
        }) || null;
    },

    saveNonRevenue: async function () {
      const mode = this.formMode.nonRevenue;

      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');

        return;
      }

      const movementType = String(this.selectedNonRevenue['Movement'] || '').trim();

      if (!movementType) {
        this.$showAlert('warning', 'Movement Type Required', 'Please enter a Movement Type.');

        return;
      }

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: comCode,
        movementtype: movementType,
        movementtypedesc: String(this.selectedNonRevenue['Description'] || '').trim(),
        driverfix: this.selectedNonRevenue['Driver Rate($)'],
        driverpercent: this.selectedNonRevenue['Driver Rate(%)'],
        attnfix: this.selectedNonRevenue['Attendant Rate($)'],
        attnpercent: this.selectedNonRevenue['Attendant Rate(%)'],
        driverpercenttype: String(this.selectedNonRevenueTariff?.value ?? 0),
        attnpercenttype: String(this.selectedNonRevenueAttTariff?.value ?? 0),
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Non-Revenue Incentive' : 'Updating Non-Revenue Incentive');

      const result = await this.$apiCallV2('/Manage/SaveNonRevInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);

        return;
      }

      this.editMode.nonRevenue = false;
      this.formMode.nonRevenue = '';

      const commission = this.commissionList.find((item) => {
        return item['Com Code'] == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert('success', mode == 'new' ? 'Created' : 'Updated', result[0]['ErrMsg']);
    },

    deleteNonRevenue: async function () {
      if (!this.selectedNonRevenueRows || this.selectedNonRevenueRows.length == 0) {
        return;
      }

      const selectedRow = this.selectedNonRevenueRows[0];
      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();
      const movementType = String(selectedRow['Movement'] || '').trim();

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Non-Revenue Incentive',
        `Are you sure you want to delete Movement Type <b>${movementType}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: comCode,
        movementtype: movementType,
        movementtypedesc: '',
        driverfix: '0',
        driverpercent: '0',
        attnfix: '0',
        attnpercent: '0',
        driverpercenttype: '0',
        attnpercenttype: '0',
      };

      this.$fullscreenLoading('Deleting Non-Revenue Incentive');

      const result = await this.$apiCallV2('/Manage/SaveNonRevInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);

        return;
      }

      const commission = this.commissionList.find((item) => {
        return item['Com Code'] == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert('success', 'Deleted', result[0]['ErrMsg']);
    },
    editFixedZone: function () {
      if (!this.selectedFixedZoneRows || this.selectedFixedZoneRows.length == 0) {
        return;
      }

      this.selectedFixedZone = JSON.parse(JSON.stringify(this.selectedFixedZoneRows[0]));

      this.selectedFixedFromZone =
        this.zoneList.find((zone) => {
          return zone['Tariff Code'] == this.selectedFixedZone['From'];
        }) || null;

      this.selectedFixedToZone =
        this.zoneList.find((zone) => {
          return zone['Tariff Code'] == this.selectedFixedZone['To'];
        }) || null;

      this.formMode.fixedZone = 'edit';
      this.editMode.fixedZone = true;
    },

    cancelFixedZone: function () {
      this.editMode.fixedZone = false;
      this.formMode.fixedZone = '';

      this.syncSelectedRows();
    },

    saveFixedZone: async function () {
      const mode = this.formMode.fixedZone;

      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');

        return;
      }

      if (!this.selectedFixedFromZone || !this.selectedFixedToZone) {
        this.$showAlert('warning', 'Zone Required', 'Please select From Zone and To Zone.');

        return;
      }

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: comCode,
        id: String(this.selectedFixedZone['ID'] || ''),
        fromarea: String(this.selectedFixedFromZone['Tariff Code'] || ''),
        toarea: String(this.selectedFixedToZone['Tariff Code'] || ''),
        driverfixedincentive: this.selectedFixedZone['Driver'],
        attendantfixedincentive: this.selectedFixedZone['Attendant'],
        driverotherincentive: this.selectedFixedZone['Driver %'],
        attendantotherincentive: this.selectedFixedZone['Attendant %'],
        b2bcoeff: this.selectedFixedZone['B2B Coeff'],
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Fixed-Zone Incentive' : 'Updating Fixed-Zone Incentive');

      const result = await this.$apiCallV2('/Manage/SaveFixedZoneInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);

        return;
      }

      this.editMode.fixedZone = false;
      this.formMode.fixedZone = '';

      const commission = this.commissionList.find((item) => {
        return item['Com Code'] == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert('success', mode == 'new' ? 'Created' : 'Updated', result[0]['ErrMsg']);
    },

    deleteFixedZone: async function () {
      if (!this.selectedFixedZoneRows || this.selectedFixedZoneRows.length == 0) {
        return;
      }

      const selectedRow = this.selectedFixedZoneRows[0];

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Fixed-Zone Incentive',
        'Are you sure you want to delete this Fixed-Zone Incentive?',
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: this.selectedCommission['Com Code'],
        id: String(selectedRow['ID'] || ''),
        fromarea: '',
        toarea: '',
        driverfixedincentive: '0',
        attendantfixedincentive: '0',
        driverotherincentive: '0',
        attendantotherincentive: '0',
        b2bcoeff: '0',
      };

      this.$fullscreenLoading('Deleting Fixed-Zone Incentive');

      const result = await this.$apiCallV2('/Manage/SaveFixedZoneInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);

        return;
      }

      await this.loadCommissionDetails(this.selectedCommission);

      this.$showAlert('success', 'Deleted', result[0]['ErrMsg']);
    },
    editIncremental: function () {
      if (!this.selectedIncrementalRows || this.selectedIncrementalRows.length == 0) {
        return;
      }

      this.selectedIncremental = JSON.parse(JSON.stringify(this.selectedIncrementalRows[0]));

      this.selectedIncrementType =
        this.incrementTypeOptions.find((item) => {
          return item.label == this.selectedIncremental['IncrementType'];
        }) || this.incrementTypeOptions[0];

      this.formMode.incremental = 'edit';
      this.editMode.incremental = true;
    },

    cancelIncremental: function () {
      this.editMode.incremental = false;
      this.formMode.incremental = '';

      this.syncSelectedRows();
    },

    saveIncremental: async function () {
      const mode = this.formMode.incremental;

      const comCode = String(this.selectedCommission?.['Com Code'] || '').trim();

      if (!comCode) {
        this.$showAlert('warning', 'Commission Code Required', 'Please select a Commission Code.');

        return;
      }

      const tripFrom = String(this.selectedIncremental['Trip From'] || '').trim();

      if (!tripFrom) {
        this.$showAlert('warning', 'Trip From Required', 'Please enter Trip From.');

        return;
      }

      const requestBody = {
        action: mode == 'new' ? 'N' : 'E',
        comcode: comCode,
        incentivefix: this.selectedIncremental['Driver Rate($)'],
        incentivepercent: this.selectedIncremental['Driver Rate(%)'],
        attnincentivefix: this.selectedIncremental['Attendant Rate($)'],
        attnincentivepercent: this.selectedIncremental['Attendant Rate(%)'],
        incentivepercentlocal: this.selectedIncremental['Driver Local(%)'],
        incentivepercentmedium: this.selectedIncremental['Driver Medium(%)'],
        incentivepercentoutstation: this.selectedIncremental['Driver Outstation(%)'],
        incentivefixlocal: this.selectedIncremental['Driver Local($)'],
        incentivefixmedium: this.selectedIncremental['Driver Medium($)'],
        incentivefixoutstation: this.selectedIncremental['Driver Outstation($)'],
        triprangefrom: tripFrom,
        incrementtype: String(this.selectedIncrementType?.value ?? 0,),
      };

      this.$fullscreenLoading(mode == 'new' ? 'Creating Incremental Incentive' : 'Updating Incremental Incentive');

      const result = await this.$apiCallV2('/Manage/SaveAddIncrementalInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Save Failed', result[0]['ErrMsg']);

        return;
      }

      this.editMode.incremental = false;
      this.formMode.incremental = '';

      const commission = this.commissionList.find((item) => {
        return item['Com Code'] == comCode;
      });

      if (commission) {
        await this.loadCommissionDetails(commission);
      }

      this.$showAlert('success', mode == 'new' ? 'Created' : 'Updated', result[0]['ErrMsg']);
    },

    deleteIncremental: async function () {
      if (!this.selectedIncrementalRows || this.selectedIncrementalRows.length == 0) {
        return;
      }

      const selectedRow = this.selectedIncrementalRows[0];
      const tripFrom = String(selectedRow['Trip From'] || '').trim();

      const confirmDelete = await this.$showAlert(
        'warning',
        'Delete Incremental Incentive',
        `Are you sure you want to delete Trip From <b>${tripFrom}</b>?`,
        [
          {
            text: 'Delete',
            color: 'red',
            textColor: 'white',
          },
          {
            text: 'Cancel',
            color: 'lightgray',
            textColor: 'black',
          },
        ],
      );

      if (confirmDelete == 'Cancel') {
        return;
      }

      const requestBody = {
        action: 'D',
        comcode: this.selectedCommission['Com Code'],
        incentivefix: '0',
        incentivepercent: '0',
        attnincentivefix: '0',
        attnincentivepercent: '0',
        incentivepercentlocal: '0',
        incentivepercentmedium: '0',
        incentivepercentoutstation: '0',
        incentivefixlocal: '0',
        incentivefixmedium: '0',
        incentivefixoutstation: '0',
        triprangefrom: tripFrom,
        incrementtype: String(selectedRow['IncrementType'] ?? this.selectedIncrementType?.value ?? 0),
      };

      this.$fullscreenLoading('Deleting Incremental Incentive');

      const result = await this.$apiCallV2('/Manage/SaveAddIncrementalInc', requestBody);

      this.$fullscreenLoading();

      if (result[0]['Results'] == '1') {
        this.$showAlert('error', 'Delete Failed', result[0]['ErrMsg']);

        return;
      }

      await this.loadCommissionDetails(this.selectedCommission);

      this.$showAlert('success', 'Deleted', result[0]['ErrMsg']);
    },

    deleteSelected: async function (section) {
      if (section == 'nonRevenue') {
        await this.deleteNonRevenue();
        return;
      }

      if (section == 'fixedZone') {
        await this.deleteFixedZone();
        return;
      }

      if (section == 'incremental') {
        await this.deleteIncremental();
        return;
      }

      this.$showAlert('warning', 'Delete', `Delete action for ${section} is not available yet.`);
    },
    driverCoeffModeChanged: function (selected) {
      this.driverCoeffMode = selected ? selected.label : '';
    },
    incrementTypeChanged: function (selected) {
      this.selectedIncrementType = selected;

      if (this.selectedIncremental) {
        this.selectedIncremental.IncrementType =
          selected ? selected.value : 0;
      }
    },
    amountTypeSelected: function (cell, selected) {
      const type = selected?.label || '$';

      cell.selectedType = selected;
      cell.type = type;

      const decimals = type == '%' ? 5 : 2;

      cell.value = this.formatDecimalValue(cell.value, decimals);

      if (type == '%') {
        cell.IncentivePercent = cell.value || '0.00000';

        cell.IncentiveFix = '0.00';
      } else {
        cell.IncentiveFix = cell.value || '0.00';

        cell.IncentivePercent = '0.00000';
      }
    },
    driverTierTypeSelected: function (tier, selected) {
      tier.type = selected ? selected.label : '';
    },
    isLastMovement: function (movement) {
      return movement == this.movementTypes[this.movementTypes.length - 1];
    },
    mapRevMvtInc: function (revDetails) {
      const jobTypes = [
        { code: 'IMP', label: 'IMPORT' },
        { code: 'EXP', label: 'EXPORT' },
        { code: '1-IMP', label: '1-WAY IMPORT' },
        { code: '1-EXP', label: '1-WAY EXPORT' },
        { code: 'IMP-EXP', label: 'IMPORT-EXPORT' },
        { code: 'MTR', label: 'EMPTY-REJECT' },
        { code: 'REPO', label: 'REPOSITION' },
        { code: '1-IMP-MT', label: '1-WAY IMP (MT)' },
        { code: '1-EXP-MT', label: '1-WAY EXP (MT)' },
      ];

      const disabledCombinations = [
        { JobType: '1-IMP', MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'] },
        { JobType: '1-EXP', MovementTypes: ['DRD', 'D1W', 'DSS', 'DSE'] },
        { JobType: 'REPO', MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'] },
        { JobType: '1-IMP-MT', MovementTypes: ['DRD', 'D1W', 'DSS', 'DSE'] },
        { JobType: '1-EXP-MT', MovementTypes: ['DRD', 'C1W', 'CSS', 'CSE'] },
      ];

      const isFixedDisabled = function (jobType, movementType) {
        return disabledCombinations.some((item) => {
          return item.JobType == jobType && item.MovementTypes.includes(movementType);
        });
      };

      return jobTypes.map((jobType) => {
        const row = {
          JobTypeCode: jobType.code,
          'Job Type': jobType.label,
        };

        this.movementTypes.forEach((movementType) => {
          const data = revDetails.find((item) => {
            return (
              String(item.JobType || '').trim() == jobType.code &&
              String(item.MovementType || '').trim() == movementType
            );
          });

          const disabled = isFixedDisabled(jobType.code, movementType);
          const incentiveType = String(data?.IncentiveType || '$').trim() || '$';
          const incentiveValue = data?.IncentiveValue || '0.00';

          row[movementType] = {
            value: disabled ? '' : incentiveValue,
            type: incentiveType,
            selectedType: { label: incentiveType },
            disabled: disabled,
            JTypeMvt: data?.JTypeMvt || `${jobType.code}${movementType}`,
            JobType: jobType.code,
            MovementType: movementType,
            IncentiveFix: data?.IncentiveFix || '0.00',
            IncentivePercent: data?.IncentivePercent || '0.00000',
          };
        });

        return row;
      });
    },
    mapDiv: function (revDetails) {
      return this.diversionTypes.map((divType) => {
        const firstDiv = revDetails.find((item) => {
          return String(item.MovementType || '').trim() == divType;
        });

        const incentiveType = String(firstDiv?.IncentiveType || '$').trim() || '$';
        const incentiveValue = firstDiv?.IncentiveValue || (incentiveType == '%' ? '0.00000' : '0.00');

        return {
          key: divType,

          value: incentiveValue,

          type: incentiveType,

          selectedType: {
            label: incentiveType,
          },
        };
      });
    },
    commissionSelected: async function (selected) {
      if (!selected) {
        return;
      }

      const comCode = String(selected['Com Code'] || '').trim();

      if (!comCode) {
        return;
      }

      const normalCommission = this.commissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      const driverCommission = this.driverCommissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      if (!normalCommission) {
        return;
      }

      this.selectedDriverCommission = driverCommission || null;

      this.driverCommissionCode = comCode;

      this.driverCommissionDescription = normalCommission['Remark'] || '';

      await this.loadCommissionDetails(normalCommission);
    },
    createCommissionDetail: function (comHeader, comType, seqNo) {
      return {
        Com_Code: comHeader.Com_Code || '',
        Com_Type: comType,
        Seq_No: seqNo,
        Range: '0',
        Driver: '0',
        Attendant: '0',
        Qlf: '$',
        IncEvery: '0',
      };
    },

    mapCommissionStructure: function (result) {
      const comHeader = result && result.ComHeader ? result.ComHeader : { ComDetails: [] };

      if (!Array.isArray(comHeader.ComDetails)) {
        comHeader.ComDetails = [];
      }

      let monthlyBonus = comHeader.ComDetails.find((detail) => {
        return String(detail.Com_Type || '').trim() == '3';
      });

      if (!monthlyBonus) {
        monthlyBonus = this.createCommissionDetail(comHeader, '3', '1');
        comHeader.ComDetails.push(monthlyBonus);
      }

      let reserve = comHeader.ComDetails.find((detail) => {
        return String(detail.Com_Type || '').trim() == '4';
      });

      if (!reserve) {
        reserve = this.createCommissionDetail(comHeader, '4', '2');
        comHeader.ComDetails.push(reserve);
      }

      return {
        ComHeader: comHeader,
        monthlyBonus: monthlyBonus,
        reserve: reserve,
      };
    },

    commissionTypeSelected: function (section, selected) {
      if (!this.coefficientSettingsDetail[section]) return;

      const type = selected?.label;

      this.coefficientSettingsDetail[section].Qlf = type;

      if (section == 'monthlyBonus') {
        this.selectedMonthlyType = { label: type };
      }

      if (section == 'reserve') {
        this.selectedReserveType = { label: type };
      }
    },

    buildCommissionPayload: function () {
      const comHeader = {
        ...this.coefficientSettingsDetail.ComHeader,

        Com_Code: String(this.driverCommissionCode || '').trim(),

        Remark: String(this.driverCommissionDescription || '').trim(),
      };

      return {
        ComHeader: comHeader,
      };
    },

    buildRevenueMovementPayload: function () {
      const payload = [];

      this.revMvtIncDet.forEach((row) => {
        this.movementTypes.forEach((movementType) => {
          const cell = row[movementType];

          if (!cell || cell.disabled) {
            return;
          }

          const type = this.getAmountType(cell) || '$';
          const value = this.formatDecimalValue(cell.value, type == '%' ? 5 : 2);

          payload.push({
            JTypeMvt: cell.JTypeMvt || `${row.JobTypeCode}${movementType}`,
            JobType: row.JobTypeCode,
            MovementType: movementType,
            IncentiveType: type,
            IncentiveValue: value,
            IncentiveFix: type == '$' ? value : '0.00',
            IncentivePercent: type == '%' ? value : '0.00000',
          });
        });
      });

      const diversionJobTypes = ['1-EXP', '1-EXP-MT', '1-IMP', 'EXP', 'IMP', 'IMP-EXP', 'MTR'];

      this.diversionDet.forEach((diversion) => {
        const type = this.getAmountType(diversion) || '$';
        const value = this.formatDecimalValue(diversion.value, type == '%' ? 5 : 2);

        diversionJobTypes.forEach((jobType) => {
          payload.push({
            JTypeMvt: `${jobType}${diversion.key}`,
            JobType: jobType,
            MovementType: diversion.key,
            IncentiveType: type,
            IncentiveValue: value,
            IncentiveFix: type == '$' ? value : '0.00',
            IncentivePercent: type == '%' ? value : '0.00000',
          });
        });
      });

      return payload;
    },

    driverCommissionSelected: async function (selected) {
      if (!selected) {
        return;
      }

      if (this.formMode.driverCommission == 'new') {
        return;
      }

      const comCode = String(selected['Com Code'] || '').trim();

      if (!comCode) {
        return;
      }

      const normalCommission = this.commissionList.find((item) => {
        return String(item['Com Code'] || '').trim() == comCode;
      });

      if (!normalCommission) {
        return;
      }

      this.selectedCommission = normalCommission;

      this.driverCommissionCode = comCode;

      this.driverCommissionDescription = normalCommission['Remark'] || '';

      await this.loadCommissionDetails(normalCommission);
    },

    refreshCommissionList: async function (comCode) {
      const result = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'LORRYCOMISSION',
      });

      this.commissionList = result.map((com) => ({
        ...com,
        'Code - Description': `${com['Com Code']} - ${com['Remark']}`,
      }));

      this.driverCommissionList = result.map((com) => ({
        ...com,
      }));

      const selectedCommission =
        this.commissionList.find((com) => {
          return String(com['Com Code'] || '').trim() == String(comCode || '').trim();
        }) ||
        this.commissionList[0] ||
        null;

      if (!selectedCommission) {
        this.selectedCommission = null;
        this.selectedDriverCommission = null;
        this.driverCommissionCode = '';
        this.driverCommissionDescription = '';
        this.coefficientSettingsDetail = {};
        return;
      }

      this.syncCommissionSelectors(selectedCommission['Com Code']);

      await this.loadCommissionDetails(selectedCommission);
    },

    getSelectedCommissionCode: function () {
      return String(
        this.selectedCommission?.['Com Code'] ||
          this.selectedDriverCommission?.['Com Code'] ||
          this.driverCommissionCode ||
          '',
      ).trim();
    },

    getAmountType: function (item) {
      return item?.selectedType?.label;
    },

    getAmountDecimals: function (item) {
      return this.getAmountType(item) == '%' ? 5 : 2;
    },

    getAmountValidator: function (item) {
      const decimals = this.getAmountDecimals(item);
      return `decimal,11,${decimals}`;
    },

    formatDecimalValue: function (value, decimals) {
      const rawValue = String(value || '')
        .replace(/,/g, '')
        .trim();

      if (rawValue === '') {
        return Number(0).toFixed(decimals);
      }

      const numberValue = Number(rawValue);

      if (Number.isNaN(numberValue)) {
        return Number(0).toFixed(decimals);
      }

      return numberValue.toFixed(decimals);
    },

    formatAmountCell: function (cell) {
      const decimals = this.getAmountDecimals(cell);
      cell.value = this.formatDecimalValue(cell.value, decimals);
    },

    formatNonRevenueAmount: function (field, decimals) {
      if (!this.selectedNonRevenue) return;

      this.selectedNonRevenue[field] = this.formatDecimalValue(this.selectedNonRevenue[field], decimals);
    },

    syncCommissionSelectors: function (comCode) {
      const code = String(comCode || '').trim();

      if (!code) {
        this.selectedCommission = null;
        this.selectedDriverCommission = null;
        this.driverCommissionCode = '';
        this.driverCommissionDescription = '';
        return;
      }

      const normalCommission =
        this.commissionList.find((item) => {
          return String(item['Com Code'] || '').trim() == code;
        }) || null;

      const driverCommission =
        this.driverCommissionList.find((item) => {
          return String(item['Com Code'] || '').trim() == code;
        }) || null;

      this.selectedCommission = normalCommission;
      this.selectedDriverCommission = driverCommission;

      this.driverCommissionCode = code;

      this.driverCommissionDescription = normalCommission?.['Remark'] || driverCommission?.['Remark'] || '';
    },

    loadCommissionDetails: async function (newVal) {
      if (!newVal) {
        return;
      }

      this.editMode.driverCommission = false;
      this.formMode.driverCommission = '';
      this.driverCommissionBackup = null;

      this.editMode.revenueMovement = false;
      this.formMode.revenueMovement = '';
      this.revenueMovementBackup = null;

      this.tableLoading = true;
      this.$fullscreenLoading('Populating List');

      this.nonRevIncList = [];
      this.selectedNonRevenueRows = [];

      this.nonRevIncList = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'LOADFIXINC',
        action: newVal['Com Code'],
      });

      this.$nextTick(() => {
        this.selectedNonRevenueRows = this.nonRevIncList[0] ? [this.nonRevIncList[0]] : [];
      });

      const revDetails = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'GETINCENTIVECOEFF',
        action: newVal['Com Code'],
      });

      this.revMvtIncList = revDetails || [];

      this.revMvtIncDet = this.mapRevMvtInc(this.revMvtIncList);

      this.diversionDet = this.mapDiv(this.revMvtIncList);

      this.fixedZoneIncentives = [];
      this.selectedFixedZoneRows = [];

      this.fixedZoneIncentives = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'GETFIXEDZONEINCENTIVES',
        action: newVal['Com Code'],
      });

      this.$nextTick(() => {
        this.selectedFixedZoneRows = this.fixedZoneIncentives[0] ? [this.fixedZoneIncentives[0]] : [];
      });

      this.incrementalIncentives = [];
      this.selectedIncrementalRows = [];

      this.incrementalIncentives = await this.$apiCallV2('/Master/GetListMaster', {
        masterfile: 'GETINCREMENTALINCENTIVE',
        action: newVal['Com Code'],
      });

      this.$nextTick(() => {
        this.selectedIncrementalRows = this.incrementalIncentives[0] ? [this.incrementalIncentives[0]] : [];
      });

      const commissionDetails = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'GETCOMMISSIONDETAILS',

        recordid: newVal['Com Code'],
      });

      this.coefficientSettingsDetail = this.mapCommissionStructure(commissionDetails);

      this.selectedMonthlyType = {
        label: this.coefficientSettingsDetail.monthlyBonus.Qlf,
      };

      this.selectedReserveType = {
        label: this.coefficientSettingsDetail.reserve.Qlf,
      };

      this.syncSelectedRows();
      this.tableLoading = false;
      this.$fullscreenLoading();
    },

    syncSelectedRows: function () {
      this.selectedNonRevenue = this.selectedNonRevenueRows[0] || {};
      this.selectedFixedZone = this.selectedFixedZoneRows[0] || {};

      this.selectedFixedFromZone =
        this.zoneList.find((zone) => {
          return zone['Tariff Code'] == this.selectedFixedZone['From'];
        }) || null;

      this.selectedFixedToZone =
        this.zoneList.find((zone) => {
          return zone['Tariff Code'] == this.selectedFixedZone['To'];
        }) || null;

      this.selectedIncremental = this.selectedIncrementalRows[0] || {};

      this.selectedIncrementType =
        this.incrementTypeOptions.find((item) => {
          return String(item.value) ==
            String(
              this.selectedIncremental[
                'IncrementType'
              ],
            );
        }) ||
        this.incrementTypeOptions[0];

      this.selectedSpecialDayDate =
        this.selectedSpecialDay['Day'] ? this.selectedSpecialDay['Day'].toDate() : this.selectedSpecialDayDate;

      this.selectedOccurrenceType =
        this.occurrenceOptions.find((item) => {
          return item.label == this.selectedSpecialDay['Occur'];
        }) || this.selectedOccurrenceType;

      this.selectedDayRemark =
        this.dayRemarkOptions.find((item) => {
          return item.label == this.selectedSpecialDay['Day remarks'];
        }) || this.selectedDayRemark;

      this.selectedSpecialMovementType =
        this.specialMovementTypeOptions.find((item) => {
          return item.label == this.selectedSpecialDay['Mvt Type'];
        }) || this.selectedSpecialMovementType;

      this.selectedMovementScope =
        this.movementScopeOptions.find((item) => {
          return item.label == this.selectedSpecialDay['Movement Scope'];
        }) || this.selectedMovementScope;

      this.selectedIncentiveType =
        this.incentiveTypeOptions.find((item) => {
          return item.label == this.selectedSpecialDay.Type;
        }) || this.selectedIncentiveType;

      this.selectedBase =
        this.baseOptions.find((item) => {
          return item.label == this.selectedSpecialDay.Base;
        }) || this.selectedBase;
    },
  },
  async mounted() {
    this.$fullscreenLoading('Checking Security Access');
    let sasControl = [];
    sasControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=HMS&page=MNG`);

    this.menu = this.menu.map((menu) => ({
      ...menu,
      enabled: true,
    }));

    sasControl.forEach((s) => {
      const menu = this.menu.find((m) => m.key == s.Control);

      if (menu) {
        const property = String(s.Properties || '').toLowerCase();
        const value = String(s.PropertiesValue || '').toLowerCase();

        if (property == 'tabenabled') {
          menu.enabled = value !== 'false';
        } else {
          menu[property] = value !== 'false';
        }

        if (menu.enabled == false) {
          menu.message = 'Access Denied';
        }
      }
    });
    //temporary disabled
    const developmentPages = ['Coefficient Settings', 'Special Day Incentive'];

    this.menu = this.menu.map((menu) => {
      if (!developmentPages.includes(menu.title)) {
        return menu;
      }

      return {
        ...menu,
        enabled: false,
        message: 'Development in progress',
      };
    });
    this.$fullscreenLoading();

    let mountedPromises = [];
    let promisesFulfilled = 0;

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'LORRYCOMISSION' }).then((result) => {
        this.commissionList = result.map((com) => ({
          ...com,
          'Code - Description': `${com['Com Code']} - ${com['Remark']}`,
        }));

        this.driverCommissionList = result.map((com) => ({
          ...com,
        }));
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CHAMAREACODE' }).then((result) => {
        this.zoneList = result.map((zone) => ({
          ...zone,
          'Code - Description': `${zone['Tariff Code']} - ${zone['Area']}`,
        }));
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises);

    const firstCommission = this.commissionList[0] || null;

    if (firstCommission) {
      this.syncCommissionSelectors(firstCommission['Com Code']);
      await this.loadCommissionDetails(firstCommission);
    }

    this.selectedSpecialDayRows = [this.specialDayIncentives[0]];
    this.syncSelectedRows();
  },
  watch: {
    selectedNonRevenueRows: function () {
      this.syncSelectedRows();

      const selectedRow = this.selectedNonRevenueRows[0];

      if (!selectedRow) {
        this.selectedNonRevenueTariff = null;
        this.selectedNonRevenueAttTariff = null;
        return;
      }

      this.selectedNonRevenueTariff =
        this.tariffDropdown.find((i) => {
          return i.value == selectedRow['IncentivePercentType'];
        }) || null;

      this.selectedNonRevenueAttTariff =
        this.tariffDropdown.find((i) => {
          return i.value == selectedRow['AttnIncentivePercentType'];
        }) || null;
    },
    selectedFixedZoneRows: function () {
      this.syncSelectedRows();
    },
    selectedFixedFromZone: function (val) {
      if (this.selectedFixedZone && val) {
        this.selectedFixedZone['From'] = val['Tariff Code'];
      }
    },

    selectedFixedToZone: function (val) {
      if (this.selectedFixedZone && val) {
        this.selectedFixedZone['To'] = val['Tariff Code'];
      }
    },
    selectedIncrementalRows: function () {
      this.syncSelectedRows();
    },
    selectedSpecialDayRows: function () {
      this.syncSelectedRows();
    },
    selectedOccurrenceType: function (val) {
      if (this.selectedSpecialDay && val) this.selectedSpecialDay['Occur'] = val.label;
    },
    selectedDayRemark: function (val) {
      if (this.selectedSpecialDay && val) this.selectedSpecialDay['Day remarks'] = val.label;
    },
    selectedSpecialMovementType: function (val) {
      if (this.selectedSpecialDay && val) this.selectedSpecialDay['Mvt Type'] = val.label;
    },
    selectedMovementScope: function (val) {
      if (this.selectedSpecialDay && val) this.selectedSpecialDay['Movement Scope'] = val.label;
    },
    selectedIncentiveType: function (val) {
      if (this.selectedSpecialDay && val) this.selectedSpecialDay.Type = val.label;
    },
    selectedBase: function (val) {
      if (this.selectedSpecialDay && val) this.selectedSpecialDay.Base = val.label;
    },
    selectedSpecialDayDate: function (val) {
      if (this.selectedSpecialDay && val && val.formatString)
        this.selectedSpecialDay['Day'] = val.formatString('dd/MM/yyyy');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.manage-main {
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  font-size: 12px;
  display: flex;
  overflow: hidden;

  > .left-menu {
    display: flex;
    justify-content: center;
  }

  > .manage-workspace {
    width: 100%;
    margin-left: 5px;
    overflow: hidden;
    background: white;
    border-radius: 5px;
    padding: 5px;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  .content-header {
    min-height: 34px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 5px 8px;
    font-weight: bolder;
    border-bottom: 2px solid rgb(180, 180, 180);
  }

  .top-selector {
    width: 280px;
    min-height: 32px;

    &.driver-commission-selector {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 8px;

      .commission-code-input {
        width: 280px;
        flex-shrink: 0;
      }

      .commission-description-input {
        flex: 1;
        min-width: 0;
      }
    }
  }

  .driver-incentive-tab {
    flex: 1;
    min-height: 0;
  }

  .driver-tab-page {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    height: 100%;
    overflow: hidden;

    &.split-view {
      flex-direction: row;
    }
  }

  .coefficient-top {
    min-height: 290px;
    max-height: 45%;
    display: grid;
    grid-template-columns: minmax(260px, 0.8fr) minmax(520px, 1.2fr);
    gap: 5px;
  }

  .price-panel,
  .movement-panel,
  .coefficient-settings-bottom {
    min-height: 0;
  }

  .coefficient-settings-bottom {
    flex: 1.1;
  }

  .coefficient-settings-detail {
    gap: 8px;
  }

  .commission-loading {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #e9e9e9;
  }

  .fill-panel,
  .tablebox-area {
    flex: 1;
    min-height: 0;
  }

  .tablebox-area {
    overflow: hidden;
  }

  .split-view {
    flex-direction: row;
    min-height: 0;
  }

  .list-side {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    min-width: 0;
    flex: 1;
  }

  .right-menu {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
    border: 1px solid #cbd3ff;
    border-radius: 5px;
    background-color: #f8f9ff;
    width: 300px;
    min-width: 300px;
    flex-shrink: 0;
    padding: 5px;
    overflow: hidden;

    ::v-deep .box-container-main {
      margin-top: 10px;
      background-color: #fff;
    }

    .bottom-actions.vertical {
      min-height: auto;
      padding: 0;
      border: none;
      background-color: transparent;

      button {
        width: 100%;
        min-height: 28px;
      }
    }
  }

  .details-panel {
    width: 100%;
    min-width: 0;
    flex: none;
  }

  .table-wrap {
    flex: 1;
    min-height: 0;
    overflow: auto;
    border: 1px solid #d8dcff;
    background: #fff;

    &.cipl-table-wrap {
      position: relative;
    }
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;

    .table-message-row {
      height: 100%;
    }

    .table-message-cell {
      height: calc(100vh - 280px);
      min-height: 300px;
      background-color: #e9e9e9;
      color: #000;
      text-align: center;
      vertical-align: top;
      padding-top: 8px;
      font-weight: normal;
      border: none;
    }

    th {
      background-color: #535cda;
      color: white;
      border: 1px solid #d8dcff;
      padding: 0 4px;
      height: 24px;
      min-height: 24px;
      text-align: center;
      white-space: nowrap;
      font-weight: 600;
      position: sticky;
      top: 0;
      z-index: 2;
    }

    td {
      border: 1px solid #d4d9ff;
      padding: 0 5px;
      height: 32px;
      min-height: 32px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    tbody tr:nth-child(odd) {
      background-color: #fff;
    }

    tbody tr:nth-child(even) {
      background-color: #e2e4ff;
    }

    tbody tr:hover {
      background-color: rgb(170, 170, 255);
    }

    input,
    select {
      width: 100%;
      min-width: 0;
      height: 24px;
      border: none;
      border-bottom: 1px solid #bfc3d6;
      background: transparent;
      font-size: 12px;
      outline: none;
    }

    input:disabled,
    select:disabled {
      color: #202020;
      opacity: 1;
    }

    &.incentive-grid {
      border-collapse: separate;
      border-spacing: 0;

      td {
        overflow: visible;
        position: relative;
        z-index: 1;
      }

      tbody tr:focus-within {
        position: relative;
        z-index: 30;
      }

      td:focus-within {
        z-index: 40;
      }
    }
  }

  .price-grid {
    th:first-child,
    td:first-child {
      width: 40px;
      text-align: center;
    }
  }

  .coefficient-grid,
  .incentive-grid {
    th {
      text-align: center;
    }
  }

  .amount-cell {
    display: grid;
    grid-template-columns: minmax(45px, 1fr) 56px;
    gap: 4px;
    align-items: center;
    position: relative;
    z-index: 1;

    &:focus-within {
      z-index: 1000;
    }
  }

  .field-grid {
    display: grid;
    gap: 5px;

    &.one {
      grid-template-columns: 1fr;
    }

    &.two {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &.three {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    label {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    select {
      height: 28px;
      border: 1px solid #c8c8c8;
    }
  }

  .details-form {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 0;
  }

  .details-rate-row {
    display: grid;
    grid-template-columns: 70px minmax(60px, 1fr) 12px minmax(60px, 1fr) minmax(120px, 1.2fr);
    gap: 5px;
    align-items: center;

    > span {
      white-space: nowrap;
    }

    &.compact-rate {
      grid-template-columns: 85px minmax(60px, 1fr) 12px minmax(60px, 1fr);
    }
  }

  .zone-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 5px;
    width: 100%;
  }

  .formula-row {
    display: grid;
    grid-template-columns:
      auto
      minmax(100px, 1fr)
      auto
      minmax(100px, 1fr)
      minmax(100px, 1fr)
      auto
      minmax(100px, 1fr)
      auto
      minmax(100px, 1fr);

    align-items: end;
    gap: 5px;
    width: 100%;

    > * {
      min-width: 0;
    }

    > span {
      white-space: nowrap;
      align-self: center;
    }

    .formula-input,
    .formula-dropdown {
      width: 100%;
      min-width: 0;
    }
  }

  .diversion-row {
    display: flex;
    gap: 5px;
    margin-top: 5px;

    label {
      display: grid;
      grid-template-columns: 45px 95px 64px;
      align-items: center;
      gap: 3px;
    }
  }

  .bottom-actions {
    border: 1px solid #cbd3ff;
    border-radius: 5px;
    background-color: #f8f9ff;
    min-height: 34px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 5px;
    padding: 5px;

    &.vertical {
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
    }
  }

  button {
    border: 1px solid #9aa8ee;
    background-color: #aebcff;
    color: #1c2458;
    border-radius: 3px;
    padding: 4px 10px;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .details-expand-enter-active,
  .details-expand-leave-active {
    transition:
      max-height 0.3s ease,
      opacity 0.3s ease,
      transform 0.3s ease;
    overflow: hidden;
  }

  .details-expand-enter,
  .details-expand-leave-to {
    max-height: 0;
    opacity: 0;
    transform: scaleY(0);
    transform-origin: top;
  }

  .details-expand-enter-to,
  .details-expand-leave {
    max-height: 600px;
    opacity: 1;
    transform: scaleY(1);
    transform-origin: top;
  }
}
</style>
